import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import RESTAURANT_DATA from "../data/restaurants.json";
import { formatMoney, formatMoneyDecimal } from "../lib/categories";
import { hashSeed } from "../lib/categoryImages";
import SmartImg from "../components/SmartImg";
import { toast } from "sonner";
import {
  Star,
  Clock,
  MapPin,
  Bike,
  ChefHat,
  Utensils,
  CheckCircle2,
  Plus,
  Minus,
  ArrowLeft,
  X,
  ShoppingBag,
} from "lucide-react";

const RESTAURANTS = RESTAURANT_DATA.restaurants;

function imgFor(tag, seed) {
  const lock = hashSeed(String(seed || tag)) % 100000;
  return `https://loremflickr.com/800/600/${tag}/all?lock=${lock}`;
}

export default function Food() {
  const [params, setParams] = useSearchParams();
  const activeId = params.get("r");
  const active = useMemo(
    () => RESTAURANTS.find((r) => r.id === activeId),
    [activeId]
  );

  if (active) {
    return (
      <RestaurantMenu
        restaurant={active}
        onBack={() => setParams({}, { replace: true })}
      />
    );
  }

  return <RestaurantList />;
}

function RestaurantList() {
  return (
    <main
      className="mx-auto max-w-[1400px] px-5 lg:px-10 py-8"
      data-testid="food-page"
    >
      <nav className="text-xs text-neutral-500 mb-3">
        <Link to="/" className="hover:text-neutral-900">Home</Link>
        <span className="mx-2">/</span>
        <span className="text-neutral-900 font-medium">Food Delivery</span>
      </nav>

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tighter">
            Food, on its way.
          </h1>
          <p className="text-neutral-600 mt-2 text-sm">
            Trending near you · Currently 12 restaurants delivering to 2831 Maple Ave.
          </p>
        </div>
        <div className="inline-flex items-center gap-2 text-xs text-neutral-600 bg-neutral-100 px-3 py-1.5 rounded-full">
          <MapPin size={14} />
          Delivering to your imaginary address
        </div>
      </div>

      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
        data-testid="restaurant-grid"
      >
        {RESTAURANTS.map((r) => (
          <Link
            key={r.id}
            to={`/food?r=${r.id}`}
            className="group border border-neutral-200 rounded-xl overflow-hidden bg-white hover-lift"
            data-testid={`restaurant-card-${r.id}`}
          >
            <SmartImg
              src={imgFor(r.heroTag, r.id)}
              alt={r.name}
              className="aspect-[16/10]"
              imgClassName="transition-transform duration-500 group-hover:scale-[1.03]"
            />
            <div className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-display font-semibold text-neutral-900 leading-tight">
                    {r.name}
                  </h3>
                  <p className="text-xs text-neutral-500 mt-0.5">{r.cuisine}</p>
                </div>
                <span className="inline-flex items-center gap-1 text-xs font-medium text-neutral-900 bg-neutral-100 px-2 py-1 rounded-full">
                  <Star size={12} className="fill-neutral-900" /> {r.rating}
                </span>
              </div>
              <p className="text-xs text-neutral-500 mt-2 line-clamp-1">{r.tagline}</p>
              <div className="mt-3 flex items-center gap-3 text-xs text-neutral-600">
                <span className="inline-flex items-center gap-1">
                  <Clock size={12} /> {r.etaMin}–{r.etaMax} min
                </span>
                <span className="inline-flex items-center gap-1">
                  <Bike size={12} /> {formatMoneyDecimal(r.deliveryFee)}
                </span>
                <span className="inline-flex items-center gap-1">
                  <MapPin size={12} /> {r.distanceMi} mi
                </span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </main>
  );
}

function RestaurantMenu({ restaurant: r, onBack }) {
  const [cart, setCart] = useState({}); // { menuItemId: qty }
  const [tracking, setTracking] = useState(null); // active order

  const menuById = useMemo(() => {
    const map = {};
    for (const m of r.menu) map[m.id] = m;
    return map;
  }, [r]);

  const subtotal = Object.entries(cart).reduce(
    (s, [id, q]) => s + (menuById[id]?.price || 0) * q,
    0
  );
  const total = subtotal + r.deliveryFee;
  const itemCount = Object.values(cart).reduce((s, q) => s + q, 0);

  const add = (id) => setCart({ ...cart, [id]: (cart[id] || 0) + 1 });
  const sub = (id) => {
    const next = { ...cart, [id]: (cart[id] || 0) - 1 };
    if (next[id] <= 0) delete next[id];
    setCart(next);
  };

  const placeOrder = () => {
    if (itemCount === 0) {
      toast.error("Add something first.");
      return;
    }
    const orderNumber = `FD-${Date.now().toString(36).toUpperCase()}`;
    setTracking({
      orderNumber,
      restaurant: r,
      items: Object.entries(cart).map(([id, q]) => ({ ...menuById[id], qty: q })),
      subtotal,
      deliveryFee: r.deliveryFee,
      total,
      startedAt: Date.now(),
    });
  };

  return (
    <main className="mx-auto max-w-[1400px] px-5 lg:px-10 py-8" data-testid="restaurant-page">
      <button
        onClick={onBack}
        className="text-xs text-neutral-500 mb-4 inline-flex items-center gap-1 hover:text-neutral-900"
        data-testid="back-to-restaurants"
      >
        <ArrowLeft size={12} /> All restaurants
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-2xl overflow-hidden border border-neutral-200 bg-white">
            <SmartImg
              src={imgFor(r.heroTag, r.id + "-hero")}
              alt={r.name}
              className="aspect-[16/7]"
            />
            <div className="p-5">
              <h1 className="font-display font-bold text-3xl tracking-tight">
                {r.name}
              </h1>
              <p className="text-sm text-neutral-500 mt-1">{r.cuisine}</p>
              <p className="text-sm text-neutral-700 mt-3">{r.tagline}</p>
              <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-neutral-600">
                <span className="inline-flex items-center gap-1"><Star size={12} className="fill-neutral-900" /> {r.rating} · {r.reviewCount.toLocaleString()} reviews</span>
                <span className="inline-flex items-center gap-1"><Clock size={12} /> {r.etaMin}–{r.etaMax} min</span>
                <span className="inline-flex items-center gap-1"><Bike size={12} /> {formatMoneyDecimal(r.deliveryFee)} delivery</span>
                <span className="inline-flex items-center gap-1"><MapPin size={12} /> {r.distanceMi} mi away</span>
              </div>
            </div>
          </div>

          <section className="border border-neutral-200 rounded-2xl bg-white p-5">
            <h2 className="font-display font-semibold text-lg tracking-tight mb-4">
              Menu
            </h2>
            <ul className="divide-y divide-neutral-200" data-testid="menu-list">
              {r.menu.map((m) => (
                <li
                  key={m.id}
                  className="py-4 flex items-center gap-4"
                  data-testid={`menu-item-${m.id}`}
                >
                  <SmartImg
                    src={imgFor(m.tag, m.id)}
                    alt={m.name}
                    className="h-16 w-16 rounded-lg shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <div className="font-display font-semibold text-neutral-900">
                        {m.name}
                      </div>
                      <div className="font-display font-bold text-neutral-900">
                        {formatMoney(m.price)}
                      </div>
                    </div>
                    <p className="text-xs text-neutral-500 mt-0.5 line-clamp-2">
                      {m.desc}
                    </p>
                  </div>
                  <div className="inline-flex items-center h-9 rounded-full border border-neutral-300 shrink-0">
                    <button
                      onClick={() => sub(m.id)}
                      disabled={!cart[m.id]}
                      className="h-full w-8 inline-flex items-center justify-center hover:bg-neutral-100 disabled:opacity-40 transition-colors"
                      data-testid={`menu-dec-${m.id}`}
                    >
                      <Minus size={12} />
                    </button>
                    <span className="w-6 text-center text-xs font-medium" data-testid={`menu-qty-${m.id}`}>
                      {cart[m.id] || 0}
                    </span>
                    <button
                      onClick={() => add(m.id)}
                      className="h-full w-8 inline-flex items-center justify-center hover:bg-neutral-100 transition-colors"
                      data-testid={`menu-inc-${m.id}`}
                    >
                      <Plus size={12} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        </div>

        <aside className="lg:col-span-1">
          <div className="lg:sticky lg:top-32 border border-neutral-200 rounded-2xl bg-white p-5">
            <h3 className="font-display font-semibold text-lg tracking-tight">
              Your order
            </h3>
            {itemCount === 0 ? (
              <p className="text-sm text-neutral-500 mt-3">
                Nothing added yet. Point at food, click plus.
              </p>
            ) : (
              <>
                <ul className="mt-3 space-y-2" data-testid="food-cart-lines">
                  {Object.entries(cart).map(([id, q]) => {
                    const m = menuById[id];
                    if (!m) return null;
                    return (
                      <li key={id} className="flex items-center justify-between text-sm">
                        <span className="truncate pr-2">{q} × {m.name}</span>
                        <span className="font-medium whitespace-nowrap">{formatMoney(m.price * q)}</span>
                      </li>
                    );
                  })}
                </ul>
                <dl className="mt-4 pt-4 border-t border-neutral-200 space-y-2 text-sm">
                  <div className="flex justify-between"><dt className="text-neutral-600">Subtotal</dt><dd>{formatMoney(subtotal)}</dd></div>
                  <div className="flex justify-between"><dt className="text-neutral-600">Delivery</dt><dd>{formatMoneyDecimal(r.deliveryFee)}</dd></div>
                  <div className="flex justify-between pt-2 border-t border-neutral-200 font-display font-semibold text-base">
                    <dt>Total</dt>
                    <dd data-testid="food-total">{formatMoney(total)}</dd>
                  </div>
                </dl>
              </>
            )}
            <button
              onClick={placeOrder}
              disabled={itemCount === 0}
              className="mt-5 w-full inline-flex items-center justify-center gap-2 h-12 rounded-full bg-neutral-900 text-white text-sm font-medium hover:bg-neutral-800 disabled:opacity-40 transition-colors"
              data-testid="food-order-button"
            >
              <ShoppingBag size={16} />
              Place order · {formatMoney(total || 0)}
            </button>
            <p className="text-[11px] text-neutral-500 mt-3 text-center leading-relaxed">
              Fictional food. Real dopamine. No actual delivery driver was harmed.
            </p>
          </div>
        </aside>
      </div>

      {tracking && (
        <OrderTrackingModal
          order={tracking}
          onClose={() => {
            setTracking(null);
            setCart({});
          }}
        />
      )}
    </main>
  );
}

// ---------------- ORDER TRACKING MODAL ---------------- //

const STAGES = [
  { key: "received", label: "Order received",     icon: CheckCircle2, at: 0 },
  { key: "prep",     label: "Restaurant preparing", icon: ChefHat,     at: 0.20 },
  { key: "picked",   label: "Rider picked up",    icon: Utensils,    at: 0.45 },
  { key: "otw",      label: "On the way",         icon: Bike,        at: 0.70 },
  { key: "delivered",label: "Delivered",          icon: CheckCircle2, at: 1.0 },
];

function OrderTrackingModal({ order, onClose }) {
  // Total simulated delivery time (ms). Kept short so the demo lands in ~45s.
  const TOTAL_MS = 45_000;
  const [t, setT] = useState(0); // 0..1

  useEffect(() => {
    const start = Date.now();
    const id = setInterval(() => {
      const p = Math.min(1, (Date.now() - start) / TOTAL_MS);
      setT(p);
      if (p >= 1) clearInterval(id);
    }, 250);
    return () => clearInterval(id);
  }, []);

  const currentStage = [...STAGES].reverse().find((s) => t >= s.at) || STAGES[0];
  const etaSecondsLeft = Math.max(0, Math.round((1 - t) * (TOTAL_MS / 1000)));

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-6"
      data-testid="tracking-modal"
    >
      <div className="w-full sm:max-w-2xl bg-white sm:rounded-2xl overflow-hidden max-h-[95vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-neutral-200 shrink-0">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">
              Order · #{order.orderNumber}
            </div>
            <div className="font-display font-semibold text-lg tracking-tight" data-testid="tracking-eta">
              {t < 1
                ? `Arriving in ~${etaSecondsLeft}s (simulated)`
                : "Delivered — enjoy."}
            </div>
          </div>
          <button
            onClick={onClose}
            className="h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-neutral-100"
            aria-label="Close"
            data-testid="tracking-close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Faux map */}
          <FauxMap progress={t} restaurantName={order.restaurant.name} />

          {/* Stages */}
          <ol className="grid grid-cols-5 gap-1 p-4 border-t border-neutral-200" data-testid="tracking-stages">
            {STAGES.map((s) => {
              const done = t >= s.at;
              const active = s.key === currentStage.key;
              const Icon = s.icon;
              return (
                <li
                  key={s.key}
                  className={`flex flex-col items-center text-center gap-1.5 ${
                    done ? "text-neutral-900" : "text-neutral-400"
                  }`}
                  data-testid={`tracking-stage-${s.key}`}
                  data-active={active ? "true" : "false"}
                >
                  <span
                    className={`h-9 w-9 rounded-full inline-flex items-center justify-center border transition-colors ${
                      done
                        ? "bg-neutral-900 border-neutral-900 text-white"
                        : "border-neutral-300"
                    } ${active ? "ring-4 ring-neutral-900/10" : ""}`}
                  >
                    <Icon size={16} />
                  </span>
                  <span className="text-[10px] font-medium leading-tight">
                    {s.label}
                  </span>
                </li>
              );
            })}
          </ol>

          {/* Order summary */}
          <div className="px-4 pb-4">
            <div className="border border-neutral-200 rounded-xl p-4 bg-neutral-50">
              <div className="text-xs text-neutral-500 mb-2">From {order.restaurant.name}</div>
              <ul className="space-y-1 text-sm" data-testid="tracking-items">
                {order.items.map((i) => (
                  <li key={i.id} className="flex justify-between">
                    <span>{i.qty} × {i.name}</span>
                    <span className="font-medium">{formatMoney(i.price * i.qty)}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-3 pt-3 border-t border-neutral-200 flex justify-between text-sm font-display font-semibold">
                <span>Total</span>
                <span data-testid="tracking-total">{formatMoney(order.total)}</span>
              </div>
            </div>

            {t >= 1 && (
              <button
                onClick={onClose}
                className="mt-4 w-full inline-flex items-center justify-center gap-2 h-11 rounded-full bg-neutral-900 text-white text-sm font-medium hover:bg-neutral-800 transition-colors"
                data-testid="tracking-done"
              >
                Done
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function FauxMap({ progress, restaurantName }) {
  // Restaurant top-left corner, customer bottom-right; a curvy path between.
  // We interpolate along a bezier so the scooter follows a curved route.
  const start = { x: 40, y: 60 };
  const c1 = { x: 200, y: 40 };
  const c2 = { x: 250, y: 180 };
  const end = { x: 380, y: 200 };

  const bezier = (t) => {
    const mt = 1 - t;
    return {
      x: mt * mt * mt * start.x + 3 * mt * mt * t * c1.x + 3 * mt * t * t * c2.x + t * t * t * end.x,
      y: mt * mt * mt * start.y + 3 * mt * mt * t * c1.y + 3 * mt * t * t * c2.y + t * t * t * end.y,
    };
  };

  const scooter = bezier(progress);

  return (
    <div className="relative w-full aspect-[16/10] bg-gradient-to-br from-emerald-50 via-white to-sky-50 overflow-hidden">
      {/* Stylized "streets" grid */}
      <svg
        viewBox="0 0 400 250"
        className="absolute inset-0 w-full h-full"
        preserveAspectRatio="xMidYMid slice"
      >
        {/* Street grid */}
        <defs>
          <pattern id="grid" width="24" height="24" patternUnits="userSpaceOnUse">
            <path d="M 24 0 L 0 0 0 24" fill="none" stroke="#dcefe0" strokeWidth="1" />
          </pattern>
        </defs>
        <rect width="400" height="250" fill="url(#grid)" />
        {/* Building blocks */}
        {[
          [60, 100, 40, 30], [120, 90, 30, 40], [160, 100, 50, 25],
          [240, 60, 40, 30], [310, 100, 40, 40], [330, 170, 30, 30],
          [80, 180, 60, 25], [170, 200, 40, 25], [270, 180, 30, 25],
        ].map(([x, y, w, h], i) => (
          <rect key={i} x={x} y={y} width={w} height={h} rx="3" fill="#e2e8f0" opacity="0.55" />
        ))}
        {/* Main roads */}
        <path d="M 0 130 Q 200 110 400 145" stroke="#c7d2fe" strokeWidth="10" fill="none" strokeLinecap="round" />
        <path d="M 220 0 Q 210 130 260 250" stroke="#c7d2fe" strokeWidth="10" fill="none" strokeLinecap="round" />
        {/* Route */}
        <path
          d={`M ${start.x} ${start.y} C ${c1.x} ${c1.y}, ${c2.x} ${c2.y}, ${end.x} ${end.y}`}
          stroke="#0a0a0a"
          strokeWidth="3"
          strokeDasharray="6 6"
          fill="none"
        />

        {/* Restaurant pin */}
        <g transform={`translate(${start.x}, ${start.y})`}>
          <circle r="12" fill="#0a0a0a" />
          <text x="0" y="4" textAnchor="middle" fontSize="12" fill="white">🍽</text>
        </g>
        {/* Customer pin */}
        <g transform={`translate(${end.x}, ${end.y})`}>
          <circle r="12" fill="#FF3B30" />
          <circle r="18" fill="#FF3B30" opacity="0.35" style={{ transformOrigin: "center", animation: "ping-slow 1.6s ease-out infinite" }} />
          <text x="0" y="4" textAnchor="middle" fontSize="12" fill="white">🏠</text>
        </g>
        {/* Scooter */}
        <g transform={`translate(${scooter.x}, ${scooter.y})`} style={{ transition: "transform 0.25s linear" }}>
          <circle r="14" fill="#ffffff" stroke="#0a0a0a" strokeWidth="2" />
          <text x="0" y="5" textAnchor="middle" fontSize="14">🛵</text>
        </g>
      </svg>

      {/* Labels overlay */}
      <div className="absolute top-3 left-3 bg-white/95 border border-neutral-200 rounded-full px-3 py-1 text-[11px] font-medium">
        {restaurantName}
      </div>
      <div className="absolute bottom-3 right-3 bg-white/95 border border-neutral-200 rounded-full px-3 py-1 text-[11px] font-medium">
        2831 Maple Ave
      </div>
    </div>
  );
}
